/**
 * Jackpot Hooks
 *
 * Custom React hooks for the jackpot system including pool tracking,
 * ticket management, and spin operations.
 *
 * Aligned with backend API at serverless/api/routes/jackpot.py
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { jackpotApi } from '../services/jackpotApi';
import { useAuth } from './useAuth';
import { handleError } from '../utils/errorHandler';
import { isElectronMode, isServerlessMode } from '../utils/apiMode';
import type {
  JackpotPool,
  JackpotTickets,
  SpinResult,
  SpinAllResult,
  CheckinResponse,
  EntitlementProgress,
  TicketPurchaseResponse,
} from '../types/jackpot';

/**
 * Jackpot endpoints live only on the Cloud Run serverless API. In local browser
 * dev (no Electron, no serverless flag) `buildCloudApiUrl` falls back to the
 * local Flask backend, which has no `/api/v1/jackpot/*` routes — every call
 * would 404. Mirrors the gating pattern in `useTokenHistory.ts`.
 */
function isJackpotAvailable(): boolean {
  return isServerlessMode() || isElectronMode();
}

// ============================================================================
// POOL HOOK
// ============================================================================

interface UseJackpotPoolReturn {
  /** The pool, or null when it could not be read (never a made-up 0). */
  poolAmount: number | null;
  pool: JackpotPool | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to track the current jackpot pool
 * Polls the pool amount at regular intervals
 */
export function useJackpotPool(pollInterval = 30000): UseJackpotPoolReturn {
  const [pool, setPool] = useState<JackpotPool | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPool = useCallback(async () => {
    if (!isJackpotAvailable()) {
      setIsLoading(false);
      return;
    }

    try {
      const data = await jackpotApi.getPool();
      setPool(data);
      setError(null);
    } catch (err) {
      setError(handleError(err, 'JackpotPool', 'Failed to fetch pool'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isJackpotAvailable()) {
      setIsLoading(false);
      return;
    }
    fetchPool();
    const interval = setInterval(fetchPool, pollInterval);
    return () => clearInterval(interval);
  }, [fetchPool, pollInterval]);

  return {
    poolAmount: pool?.pool_amount ?? null,
    pool,
    isLoading,
    error,
    refetch: fetchPool,
  };
}

// ============================================================================
// TICKETS HOOK
// ============================================================================

interface UseJackpotTicketsReturn {
  tickets: number;
  ticketDetails: JackpotTickets | null;
  entitlements: EntitlementProgress[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to track user's available jackpot tickets
 * Includes entitlements from the same response
 * Only fetches when user is authenticated
 */
export function useJackpotTickets(): UseJackpotTicketsReturn {
  const { user, getIdToken } = useAuth();
  const [ticketDetails, setTicketDetails] = useState<JackpotTickets | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTickets = useCallback(async () => {
    if (!user || !isJackpotAvailable()) {
      setTicketDetails(null);
      return;
    }

    const token = await getIdToken();
    if (!token) return;

    setIsLoading(true);
    try {
      const data = await jackpotApi.getTickets(token);
      setTicketDetails(data);
      setError(null);
    } catch (err) {
      setError(handleError(err, 'JackpotTickets', 'Failed to fetch tickets'));
    } finally {
      setIsLoading(false);
    }
  }, [user, getIdToken]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  return {
    tickets: ticketDetails?.available_tickets ?? 0,
    ticketDetails,
    entitlements: ticketDetails?.entitlements ?? [],
    isLoading,
    error,
    refetch: fetchTickets,
  };
}

// ============================================================================
// SPIN HOOK
// ============================================================================

interface UseSpinReturn {
  spin: (ticketId?: string) => Promise<SpinResult | null>;
  isSpinning: boolean;
  lastResult: SpinResult | null;
  error: string | null;
  clearError: () => void;
}

/**
 * Hook for spinning the jackpot wheel
 */
export function useSpin(onSuccess?: (result: SpinResult) => void): UseSpinReturn {
  const { getIdToken } = useAuth();
  const [isSpinning, setIsSpinning] = useState(false);
  const [lastResult, setLastResult] = useState<SpinResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const spin = useCallback(
    async (ticketId?: string): Promise<SpinResult | null> => {
      if (!isJackpotAvailable()) {
        setError('Jackpot is only available in the cloud or desktop app');
        return null;
      }

      const token = await getIdToken();
      if (!token) {
        setError('Authentication required');
        return null;
      }

      setIsSpinning(true);
      setError(null);

      try {
        const result = await jackpotApi.spin(token, ticketId);
        setLastResult(result);
        onSuccess?.(result);
        return result;
      } catch (err) {
        setError(handleError(err, 'JackpotSpin', 'Spin failed'));
        return null;
      } finally {
        setIsSpinning(false);
      }
    },
    [onSuccess, getIdToken]
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    spin,
    isSpinning,
    lastResult,
    error,
    clearError,
  };
}

// ============================================================================
// SPIN ALL HOOK
// ============================================================================

interface UseSpinAllReturn {
  spinAll: (maxTickets?: number) => Promise<SpinAllResult | null>;
  isSpinning: boolean;
  lastResult: SpinAllResult | null;
  error: string | null;
  clearError: () => void;
}

/**
 * Hook for spinning all available tickets at once
 */
export function useSpinAll(onSuccess?: (result: SpinAllResult) => void): UseSpinAllReturn {
  const { getIdToken } = useAuth();
  const [isSpinning, setIsSpinning] = useState(false);
  const [lastResult, setLastResult] = useState<SpinAllResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const spinAll = useCallback(
    async (maxTickets?: number): Promise<SpinAllResult | null> => {
      if (!isJackpotAvailable()) {
        setError('Jackpot is only available in the cloud or desktop app');
        return null;
      }

      const token = await getIdToken();
      if (!token) {
        setError('Authentication required');
        return null;
      }

      setIsSpinning(true);
      setError(null);

      try {
        const result = await jackpotApi.spinAll(token, maxTickets);
        setLastResult(result);
        onSuccess?.(result);
        return result;
      } catch (err) {
        setError(handleError(err, 'JackpotSpinAll', 'Spin all failed'));
        return null;
      } finally {
        setIsSpinning(false);
      }
    },
    [onSuccess, getIdToken]
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    spinAll,
    isSpinning,
    lastResult,
    error,
    clearError,
  };
}

// ============================================================================
// CHECKIN HOOK
// ============================================================================

interface UseCheckinReturn {
  checkin: () => Promise<CheckinResponse | null>;
  result: CheckinResponse | null;
  freeBonusProgress: EntitlementProgress | null;
  isChecking: boolean;
  error: string | null;
}

/**
 * Hook to process daily check-in
 * Replaces useLoginReward and useFreeBonus
 */
export function useCheckin(): UseCheckinReturn {
  const { getIdToken } = useAuth();
  const [result, setResult] = useState<CheckinResponse | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasCheckedRef = useRef(false);

  const doCheckin = useCallback(async (): Promise<CheckinResponse | null> => {
    // Prevent duplicate checks in the same session
    if (hasCheckedRef.current) {
      return result;
    }
    if (!isJackpotAvailable()) {
      return null;
    }

    const token = await getIdToken();
    if (!token) return null;

    setIsChecking(true);
    setError(null);

    try {
      const data = await jackpotApi.checkin(token);
      setResult(data);
      hasCheckedRef.current = true;
      return data;
    } catch (err) {
      setError(handleError(err, 'JackpotCheckin', 'Failed to check in'));
      return null;
    } finally {
      setIsChecking(false);
    }
  }, [result, getIdToken]);

  return {
    checkin: doCheckin,
    result,
    freeBonusProgress: result?.free_bonus_progress ?? null,
    isChecking,
    error,
  };
}

// ============================================================================
// FREE BONUS HOOK (from checkin)
// ============================================================================

interface UseFreeBonusReturn {
  progress: EntitlementProgress | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to track free bonus progress (7-day streak)
 * Gets data from checkin endpoint
 */
export function useFreeBonus(): UseFreeBonusReturn {
  const { user, getIdToken } = useAuth();
  const [progress, setProgress] = useState<EntitlementProgress | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProgress = useCallback(async () => {
    if (!user || !isJackpotAvailable()) {
      setProgress(null);
      return;
    }

    const token = await getIdToken();
    if (!token) return;

    setIsLoading(true);
    try {
      // Get free bonus from checkin endpoint
      const data = await jackpotApi.checkin(token);
      setProgress(data.free_bonus_progress);
      setError(null);
    } catch (err) {
      setError(handleError(err, 'JackpotFreeBonus', 'Failed to fetch free bonus progress'));
    } finally {
      setIsLoading(false);
    }
  }, [user, getIdToken]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  return {
    progress,
    isLoading,
    error,
    refetch: fetchProgress,
  };
}

// ============================================================================
// ENTITLEMENTS HOOK (from tickets)
// ============================================================================

interface UseEntitlementsReturn {
  entitlements: EntitlementProgress[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch user's ticket entitlements
 * Gets data from tickets endpoint
 */
export function useEntitlements(): UseEntitlementsReturn {
  const { user, getIdToken } = useAuth();
  const [entitlements, setEntitlements] = useState<EntitlementProgress[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEntitlements = useCallback(async () => {
    if (!user || !isJackpotAvailable()) {
      setEntitlements([]);
      return;
    }

    const token = await getIdToken();
    if (!token) return;

    setIsLoading(true);
    try {
      // Entitlements are included in tickets response
      const data = await jackpotApi.getTickets(token);
      setEntitlements(data.entitlements);
      setError(null);
    } catch (err) {
      setError(handleError(err, 'JackpotEntitlements', 'Failed to fetch entitlements'));
    } finally {
      setIsLoading(false);
    }
  }, [user, getIdToken]);

  useEffect(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);

  return {
    entitlements,
    isLoading,
    error,
    refetch: fetchEntitlements,
  };
}

// ============================================================================
// PURCHASE TICKETS HOOK
// ============================================================================

interface UsePurchaseTicketsReturn {
  purchase: (quantity: number) => Promise<TicketPurchaseResponse | null>;
  isPurchasing: boolean;
  lastPurchase: TicketPurchaseResponse | null;
  error: string | null;
  clearError: () => void;
}

/**
 * Hook for purchasing tickets with tokens
 */
export function usePurchaseTickets(
  onSuccess?: (result: TicketPurchaseResponse) => void
): UsePurchaseTicketsReturn {
  const { getIdToken } = useAuth();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [lastPurchase, setLastPurchase] = useState<TicketPurchaseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const purchase = useCallback(
    async (quantity: number): Promise<TicketPurchaseResponse | null> => {
      if (!isJackpotAvailable()) {
        setError('Jackpot is only available in the cloud or desktop app');
        return null;
      }

      const token = await getIdToken();
      if (!token) {
        setError('Authentication required');
        return null;
      }

      setIsPurchasing(true);
      setError(null);

      try {
        const result = await jackpotApi.purchaseTickets(token, quantity);
        setLastPurchase(result);
        onSuccess?.(result);
        return result;
      } catch (err) {
        setError(handleError(err, 'JackpotPurchase', 'Purchase failed'));
        return null;
      } finally {
        setIsPurchasing(false);
      }
    },
    [onSuccess, getIdToken]
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    purchase,
    isPurchasing,
    lastPurchase,
    error,
    clearError,
  };
}

// ============================================================================
// PREVIOUS VALUE HOOK (for animations)
// ============================================================================

/**
 * Hook to track the previous value of a state
 * Useful for animation comparisons
 */
export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

// ============================================================================
// COMBINED JACKPOT STATE HOOK
// ============================================================================

interface UseJackpotReturn {
  pool: UseJackpotPoolReturn;
  tickets: UseJackpotTicketsReturn;
  spin: UseSpinReturn;
  spinAll: UseSpinAllReturn;
  entitlements: UseEntitlementsReturn;
  freeBonus: UseFreeBonusReturn;
  purchaseTickets: UsePurchaseTicketsReturn;
  refetchAll: () => Promise<void>;
}

/**
 * Combined hook for all jackpot state
 * Provides a unified interface for the jackpot system
 */
export function useJackpot(): UseJackpotReturn {
  const pool = useJackpotPool();
  const tickets = useJackpotTickets();
  const spinHook = useSpin(() => {
    tickets.refetch();
    pool.refetch();
  });
  const spinAllHook = useSpinAll(() => {
    tickets.refetch();
    pool.refetch();
  });
  const entitlements = useEntitlements();
  const freeBonus = useFreeBonus();
  const purchaseTicketsHook = usePurchaseTickets(() => {
    tickets.refetch();
  });

  const refetchAll = useCallback(async () => {
    await Promise.all([
      pool.refetch(),
      tickets.refetch(),
      entitlements.refetch(),
      freeBonus.refetch(),
    ]);
  }, [pool, tickets, entitlements, freeBonus]);

  return {
    pool,
    tickets,
    spin: spinHook,
    spinAll: spinAllHook,
    entitlements,
    freeBonus,
    purchaseTickets: purchaseTicketsHook,
    refetchAll,
  };
}
